import { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Popover,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  HelpOutlined as HelpOutlineIcon,
  ThreeDRotation,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { get } from 'es-toolkit/compat'

import * as c from 'common/c'
import { TEST_IDS } from 'test-ids'
import {
  setThreeDSurface,
  setVerticalExaggeration,
  toggleThreeD,
} from 'actions/map'
import { supportsWebGL } from 'components/map/three-d/webgl-support'
import ThreeDHelpDialog from './help-dialog'
import type { AppState } from 'state/index.js'

const DEM_LAYER_NAME = 'dem'
// A stable fallback: a fresh [] per call makes useSelector see a new result
// every time and re-render, and defeats the useMemo keyed on it.
const NO_LAYER_INFO: never[] = []

/**
 * The 3D toggle, with the surface selector and vertical-exaggeration slider it
 * reveals once 3D is on.
 *
 * Sits beside the layers control rather than in it: 3D is a view mode rather
 * than a layer, and it has to be reachable on the overview map where no harvest
 * (and so no layers popover content) exists.
 */
export default function ThreeDControl() {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [helpOpen, setHelpOpen] = useState(false)

  const enabled = useSelector((state: AppState) => state.map.threeDEnabled)
  const surface = useSelector((state: AppState) => state.map.threeDSurface)
  const threeDStatus = useSelector((state: AppState) => state.map.threeDStatus)
  const exaggeration = useSelector(
    (state: AppState) => state.map.verticalExaggeration,
  )
  // Whether the harvest *has* a DEM, which is not the same question as whether
  // its raster is currently on screen. layers-group gates its own DEM toggle on
  // exactly this (layerInfoMap['dem']); reading map.wmsLayers instead would
  // only ever enable the option once the user had already switched the layer on
  // by hand.
  const layerInfo = useSelector((state: AppState) =>
    get(state, 'field.layerInfo', NO_LAYER_INFO),
  )
  const demAvailable = useMemo(
    () =>
      (layerInfo as { layerName?: string }[]).some(
        (layer) => layer.layerName === DEM_LAYER_NAME,
      ),
    [layerInfo],
  )

  // Must not pull in the Cesium chunk just to decide whether to offer 3D.
  const webglAvailable = useMemo(() => supportsWebGL(), [])

  const open = Boolean(anchorEl)
  const popoverId = open ? TEST_IDS.threeDControlPopover : undefined

  const loading = enabled && threeDStatus === c.THREE_D_STATUS.LOADING
  // Not gated on `enabled`: a lost WebGL context drops back to 2D, and the
  // button is the only place left to say why.
  const failed = threeDStatus === c.THREE_D_STATUS.FAILED

  const label = enabled ? t('three_d_view_disable') : t('three_d_view_enable')
  let tooltip = label
  if (!webglAvailable) tooltip = t('three_d_unavailable')
  else if (loading) tooltip = t('three_d_loading')
  else if (failed) tooltip = t('three_d_failed')

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (enabled) {
      // While 3D is on the button opens the settings; turning it off is done
      // from inside the popover, so a stray click cannot drop the user out of
      // 3D and discard the camera position.
      setAnchorEl(event.currentTarget)
      return
    }
    dispatch(toggleThreeD())
  }

  const handleExit = () => {
    setAnchorEl(null)
    dispatch(toggleThreeD())
  }

  const handleHelpOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    // MUI's modal stack marks this Popover aria-hidden once the Dialog opens on
    // top of it, because it is no longer the topmost modal. Without this blur
    // the click leaves DOM focus on this button, which is then a focused
    // descendant of an aria-hidden ancestor — invalid per the ARIA spec, and
    // exactly the warning MUI itself documents this workaround for. Moving
    // focus off the button first means there is nothing focused inside the
    // Popover for the effect to conflict with; the Dialog's own autoFocus picks
    // focus back up once it mounts.
    event.currentTarget.blur()
    setHelpOpen(true)
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 'calc(2em + 1px + 1.14 * 1.375em)',
        left: '104px',
        zIndex: 1000,
      }}
    >
      <Tooltip title={tooltip}>
        <span>
          <IconButton
            onClick={handleClick}
            disabled={!webglAvailable}
            aria-label={label}
            aria-pressed={enabled}
            aria-describedby={popoverId}
            data-testid={TEST_IDS.threeDToggleButton}
            sx={{
              // Matches layers-control: this floats alone over the map
              // rather than in a packed row, so the theme's real-padding
              // touch target isn't needed to keep neighbors apart, and a
              // 32px icon with no padding already clears WCAG's 24px
              // minimum. Overriding back to padding:0 keeps the circle
              // sized to the icon instead of the theme's default — without
              // this it renders 56px instead of 32px and sits misaligned
              // with the layers button beside it.
              padding: 0,
              // Filled while 3D is on, matching how harvest-toolbar marks its
              // selected tools — this is a mode the map is in, not a momentary
              // action, so it needs to read as engaged at a glance.
              bgcolor: enabled ? 'primary.main' : 'background.paper',
              boxShadow: 2,
              '&:hover': {
                bgcolor: enabled ? 'primary.dark' : 'grey.100',
              },
            }}
          >
            {loading ? (
              // The first toggle downloads several megabytes of CesiumJS, which
              // is long enough that an unchanged button reads as a dead click.
              <CircularProgress
                size={28}
                sx={{ color: 'common.white' }}
                data-testid={TEST_IDS.threeDLoadingIndicator}
              />
            ) : (
              <ThreeDRotation
                // Set here rather than inherited from the button: IconButton's
                // own root colour otherwise wins and leaves a grey icon that
                // disappears into the filled background.
                sx={{
                  fontSize: '2rem',
                  color: failed
                    ? 'error.main'
                    : enabled
                      ? 'common.white'
                      : 'action.active',
                }}
              />
            )}
          </IconButton>
        </span>
      </Tooltip>

      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box
          data-testid={TEST_IDS.threeDControlPopover}
          sx={{ p: 2, minWidth: 240 }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Typography variant="subtitle2">{t('three_d_surface')}</Typography>
            <Tooltip title={t('three_d_help')}>
              <IconButton
                size="small"
                onClick={handleHelpOpen}
                aria-label={t('three_d_help')}
                data-testid={TEST_IDS.threeDHelpButton}
              >
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={surface}
            onChange={(_event, value) => {
              if (value) dispatch(setThreeDSurface(value))
            }}
            aria-label={t('three_d_surface')}
            data-testid={TEST_IDS.threeDSurfaceSelector}
          >
            <ToggleButton value={c.THREE_D_SURFACE.IMAGERY}>
              {t('three_d_surface_imagery')}
            </ToggleButton>
            <Tooltip
              title={
                demAvailable ? '' : t('three_d_surface_elevation_unavailable')
              }
            >
              <span style={{ width: '100%' }}>
                <ToggleButton
                  value={c.THREE_D_SURFACE.ELEVATION}
                  disabled={!demAvailable}
                  sx={{ width: '100%' }}
                >
                  {t('three_d_surface_elevation')}
                </ToggleButton>
              </span>
            </Tooltip>
          </ToggleButtonGroup>

          <Divider sx={{ my: 2 }} />

          <FormControlLabel
            control={
              <Slider
                value={exaggeration}
                onChange={(_event, value) =>
                  dispatch(setVerticalExaggeration(value as number))
                }
                min={c.MIN_VERTICAL_EXAGGERATION}
                max={c.MAX_VERTICAL_EXAGGERATION}
                step={c.VERTICAL_EXAGGERATION_STEP}
                size="small"
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${value}×`}
                aria-label={t('vertical_exaggeration')}
                sx={{ width: '100%', mr: 1 }}
                data-testid={TEST_IDS.threeDVerticalExaggerationSlider}
              />
            }
            label={t('vertical_exaggeration')}
            labelPlacement="top"
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              ml: 0,
              width: '100%',
              '& .MuiFormControlLabel-label': {
                fontSize: '0.8125rem',
                color: 'text.secondary',
              },
            }}
          />

          <Divider sx={{ my: 1 }} />

          <Button
            fullWidth
            size="small"
            variant="outlined"
            onClick={handleExit}
          >
            {t('three_d_view_disable')}
          </Button>
        </Box>
      </Popover>

      <ThreeDHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
    </Box>
  )
}
